import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { formatTimestamp, getActiveChapterIndex } from '../../utils/videoTime';

export default function ChapterList({ chapters, currentTime = 0, onChapterClick }) {
  const [expanded, setExpanded] = useState(false);

  if (!chapters?.length) return null;

  const activeIndex = getActiveChapterIndex(chapters, currentTime);
  const activeChapter = activeIndex >= 0 ? chapters[activeIndex] : chapters[0];
  const countLabel = `${chapters.length} capítulo${chapters.length === 1 ? '' : 's'}`;

  const renderChapterButton = (chapter, index, { compact = false } = {}) => {
    const isActive = index === activeIndex;
    return (
      <button
        type="button"
        onClick={() => onChapterClick?.(chapter.startSeconds)}
        className={`w-full flex items-baseline gap-3 text-left rounded-xl transition-colors ${
          compact ? 'px-2 py-1.5' : 'px-3 py-2.5'
        } ${
          isActive
            ? 'bg-brand-cyan/10 border border-brand-cyan/30'
            : 'hover:bg-[var(--bg-surface)] border border-transparent'
        }`}
      >
        <span
          className={`text-xs font-black tabular-nums flex-shrink-0 ${
            isActive ? 'text-brand-cyan' : 'text-[var(--text-secondary)]'
          }`}
        >
          {formatTimestamp(chapter.startSeconds)}
        </span>
        <span
          className={`text-sm font-medium truncate ${
            isActive ? 'text-[var(--text-main)]' : 'text-[var(--text-secondary)]'
          }`}
        >
          {chapter.title}
        </span>
        {isActive && (
          <SafeIcon name="Play" className="w-3 h-3 text-brand-cyan ml-auto flex-shrink-0" />
        )}
      </button>
    );
  };

  return (
    <nav
      aria-label="Capítulos del video"
      className="px-4 md:px-6 py-3 border-b border-[var(--border-subtle)] bg-[var(--bg-base)]"
    >
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3 group"
      >
        <div className="flex items-center gap-2 min-w-0 text-left">
          <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] flex-shrink-0">
            Capítulos
          </h2>
          <span className="text-[10px] font-bold text-[var(--text-secondary)]/70">·</span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-secondary)]">
            {countLabel}
          </span>
        </div>
        <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-brand-cyan group-hover:text-brand-green flex-shrink-0 transition-colors">
          {expanded ? 'Ocultar' : 'Ver todos'}
          <SafeIcon
            name={expanded ? 'ChevronUp' : 'ChevronDown'}
            className="w-3.5 h-3.5"
          />
        </span>
      </button>

      {expanded ? (
        <ul className="space-y-1 mt-2 max-h-52 overflow-y-auto pr-1">
          {chapters.map((chapter, index) => (
            <li key={`${chapter.startSeconds}-${chapter.title}`}>
              {renderChapterButton(chapter, index)}
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-1.5">
          {renderChapterButton(activeChapter, activeIndex >= 0 ? activeIndex : 0, { compact: true })}
        </div>
      )}
    </nav>
  );
}
